import { useEffect, useRef } from 'react';

/* Holograma central do Jarvis: globo em malha flutuando sobre uma plataforma de
   anéis, com feixe de luz, lentes laterais e uma esfera menor. Desenhado em canvas,
   em 3D projetado, sem bibliotecas. Reage ao ponteiro (inclinação e paralaxe) e à
   voz (nível do microfone, escuta, fala). Sem servidor, tudo fica apagado. */

export interface HoloSignal {
  level: number;
  listening: boolean;
  speaking: boolean;
  alert: boolean;
  online: boolean;
}

type Variant = 'home' | 'login';

const VIEW: Record<Variant, { w: number; h: number; cx: number; plat: number; globe: number; R: number; ring: number }> = {
  home: { w: 1120, h: 640, cx: 560, plat: 410, globe: 238, R: 132, ring: 470 },
  login: { w: 640, h: 600, cx: 320, plat: 400, globe: 236, R: 124, ring: 300 }
};

const CYAN = '63,210,255';
const BLUE = '26,124,240';
const ICE = '214,244,255';
const AMBER = '255,182,72';
const TAU = Math.PI * 2;

const rgba = (c: string, a: number) => `rgba(${c},${Math.max(0, Math.min(1, a)).toFixed(3)})`;

/* Sprite de brilho pré-renderizado: muito mais barato que shadowBlur. */
function glowSprite(stops: [number, string][]) {
  const s = document.createElement('canvas');
  s.width = s.height = 256;
  const g = s.getContext('2d')!;
  const grad = g.createRadialGradient(128, 128, 0, 128, 128, 128);
  for (const [o, c] of stops) grad.addColorStop(o, c);
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 256);
  return s;
}

/* Pontos distribuídos por igual na esfera, e as arestas da malha entre vizinhos. */
function buildMesh(n: number) {
  const pts: [number, number, number][] = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const th = golden * i;
    pts.push([Math.cos(th) * r, y, Math.sin(th) * r]);
  }
  const edges: [number, number][] = [];
  const seen = new Set<string>();
  for (let i = 0; i < n; i++) {
    const d = pts.map((p, j) => [j, (p[0] - pts[i]![0]) ** 2 + (p[1] - pts[i]![1]) ** 2 + (p[2] - pts[i]![2]) ** 2] as const)
      .filter(([j]) => j !== i).sort((a, b) => a[1] - b[1]).slice(0, 3);
    for (const [j] of d) {
      const k = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (!seen.has(k)) { seen.add(k); edges.push([i, j]); }
    }
  }
  return { pts, edges };
}

function buildGrid() {
  const lines: [number, number, number][][] = [];
  for (let k = 0; k < 12; k++) {
    const lon = (k / 12) * TAU;
    const line: [number, number, number][] = [];
    for (let a = -90; a <= 90; a += 6) {
      const lat = (a * Math.PI) / 180;
      line.push([Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon)]);
    }
    lines.push(line);
  }
  for (let a = -75; a <= 75; a += 15) {
    const lat = (a * Math.PI) / 180;
    const line: [number, number, number][] = [];
    for (let d = 0; d <= 360; d += 6) {
      const lon = (d * Math.PI) / 180;
      line.push([Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon)]);
    }
    lines.push(line);
  }
  return lines;
}

interface Particle { r: number; a: number; v: number; h: number; tw: number; s: number }

export function Hologram({ variant, signal, onCore, coreLabel }: {
  variant: Variant;
  signal: HoloSignal;
  onCore?: () => void;
  coreLabel?: string;
}) {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const sig = useRef(signal);
  sig.current = signal;
  const hot = useRef(false);
  const V = VIEW[variant];

  useEffect(() => {
    const cv = cvRef.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return;

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const { w: W, h: H, cx, plat, globe, R, ring } = V;
    const home = variant === 'home';

    const sCore = glowSprite([[0, 'rgba(255,255,255,1)'], [0.12, rgba(ICE, 0.9)], [0.35, rgba(CYAN, 0.35)], [0.7, rgba(BLUE, 0.08)], [1, 'rgba(0,0,0,0)']]);
    const sHalo = glowSprite([[0, rgba(CYAN, 0.0)], [0.5, rgba(CYAN, 0.0)], [0.62, rgba(CYAN, 0.28)], [0.7, rgba(BLUE, 0.14)], [1, 'rgba(0,0,0,0)']]);
    const sSoft = glowSprite([[0, rgba(CYAN, 0.5)], [0.4, rgba(BLUE, 0.18)], [1, 'rgba(0,0,0,0)']]);
    const sAmber = glowSprite([[0, 'rgba(255,240,210,1)'], [0.25, rgba(AMBER, 0.7)], [1, 'rgba(0,0,0,0)']]);

    const mesh = buildMesh(home ? 170 : 140);
    const grid = buildGrid();
    const rings = home ? [1, 0.87, 0.73, 0.6, 0.46, 0.32, 0.2, 0.11] : [1, 0.84, 0.68, 0.52, 0.36, 0.22, 0.12];

    const parts: Particle[] = Array.from({ length: home ? 150 : 90 }, () => ({
      r: (0.12 + Math.random() * 0.95) * ring,
      a: Math.random() * TAU,
      v: (0.02 + Math.random() * 0.08) * (Math.random() < 0.5 ? 1 : -1),
      h: Math.random() * 46,
      tw: Math.random() * TAU,
      s: 0.6 + Math.random() * 1.4
    }));
    const motes = Array.from({ length: 26 }, () => ({ y: Math.random(), x: (Math.random() - 0.5) * 2, v: 0.08 + Math.random() * 0.18 }));
    const ripples: { born: number }[] = [];

    let dpr = 1, raf = 0, t = 0, last = performance.now(), lastRipple = 0;
    let mx = 0, my = 0, tmx = 0, tmy = 0, energy = 0;

    const size = () => {
      const r = cv.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      const bw = Math.max(1, Math.round(r.width * dpr));
      const bh = Math.max(1, Math.round(r.height * dpr));
      if (cv.width !== bw || cv.height !== bh) { cv.width = bw; cv.height = bh; }
      ctx.setTransform(bw / W, 0, 0, bh / H, 0, 0);
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(cv);
    window.addEventListener('resize', size);

    const onMove = (e: PointerEvent) => {
      tmx = (e.clientX / window.innerWidth - 0.5) * 2;
      tmy = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('pointermove', onMove, { passive: true });

    const sprite = (img: HTMLCanvasElement, x: number, y: number, w: number, h = w) => ctx.drawImage(img, x - w / 2, y - h / 2, w, h);

    /* ---------- Plataforma de anéis ---------- */
    const platform = (K: number, alpha: number, speed: number) => {
      const py = plat;
      sprite(sSoft, cx, py, ring * 2.3, ring * 2.3 * K * 1.15);
      sprite(sCore, cx, py, ring * 0.9, ring * 0.9 * K * 1.6);

      rings.forEach((f, i) => {
        const rx = ring * f, ry = rx * K;
        const back = 0.42, front = 1;
        const base = (i === 0 ? 0.32 : i === 5 || i === 4 ? 0.75 : 0.5) * alpha;
        for (const [a0, a1, k] of [[Math.PI, TAU, back], [0, Math.PI, front]] as const) {
          ctx.strokeStyle = rgba(i < 2 ? BLUE : CYAN, base * k + (i < 2 ? 0.15 : 0));
          ctx.lineWidth = i === 5 ? 1.6 : 1;
          ctx.beginPath(); ctx.ellipse(cx, py, rx, ry, 0, a0, a1); ctx.stroke();
        }

        const rot = t * speed * (i % 2 ? -1 : 1) * (0.25 + i * 0.05);
        if (i === 0 || i === 3) {
          /* Escala de marcações, como uma régua circular. */
          const n = i === 0 ? 180 : 96;
          ctx.beginPath();
          for (let k = 0; k < n; k++) {
            const a = (k / n) * TAU + rot * 0.3;
            const long = k % 10 === 0 ? 2.2 : 1;
            const c = Math.cos(a), s = Math.sin(a);
            const L = (i === 0 ? 7 : 5) * long;
            ctx.moveTo(cx + rx * c, py + ry * s);
            ctx.lineTo(cx + (rx + L) * c, py + (ry + L * K) * s);
          }
          ctx.strokeStyle = rgba(CYAN, 0.32 * alpha);
          ctx.stroke();
        }
        if (i === 1 || i === 6) {
          /* Segmentos tracejados girando. */
          const n = i === 1 ? 28 : 16;
          ctx.lineWidth = i === 6 ? 4 : 2.2;
          for (let k = 0; k < n; k++) {
            const a = (k / n) * TAU + rot;
            const s = Math.sin(a + 0.08);
            ctx.strokeStyle = rgba(CYAN, (0.25 + 0.55 * (s * 0.5 + 0.5)) * alpha);
            ctx.beginPath(); ctx.ellipse(cx, py, rx, ry, 0, a, a + (TAU / n) * 0.55); ctx.stroke();
          }
          ctx.lineWidth = 1;
        }
        if (i === 2) {
          /* Dois cometas de luz correndo pelo anel. */
          for (let c = 0; c < 2; c++) {
            const a = rot * 2.2 + c * Math.PI;
            const g = 14;
            for (let k = 0; k < g; k++) {
              ctx.strokeStyle = rgba(ICE, (k / g) * 0.9 * alpha);
              ctx.lineWidth = 1 + (k / g) * 1.8;
              ctx.beginPath(); ctx.ellipse(cx, py, rx, ry, 0, a + k * 0.03, a + (k + 1) * 0.03); ctx.stroke();
            }
            sprite(sCore, cx + rx * Math.cos(a + g * 0.03), py + ry * Math.sin(a + g * 0.03), 34, 34);
          }
          ctx.lineWidth = 1;
        }
        if (i === 4) {
          /* Anel de pontos. */
          ctx.fillStyle = rgba(ICE, 0.7 * alpha);
          for (let k = 0; k < 72; k++) {
            const a = (k / 72) * TAU - rot * 0.8;
            const s = Math.sin(a);
            if (k % 3 === 0) ctx.fillRect(cx + rx * Math.cos(a) - 1, py + ry * s - 1, 2, 2 * (0.6 + 0.4 * (s * 0.5 + 0.5)));
          }
        }
      });

      /* Ondas que saem do centro quando o Jarvis escuta ou fala. */
      for (const rp of ripples) {
        const age = (t - rp.born) / 2.4;
        if (age > 1) continue;
        const rx = ring * (0.1 + age * 0.95);
        ctx.strokeStyle = rgba(ICE, (1 - age) * 0.55 * alpha);
        ctx.lineWidth = 2 * (1 - age) + 0.5;
        ctx.beginPath(); ctx.ellipse(cx, py, rx, rx * K, 0, 0, TAU); ctx.stroke();
      }
      ctx.lineWidth = 1;

      /* Lente anamórfica: o risco de luz horizontal que atravessa a base. */
      const fl = ctx.createLinearGradient(cx - ring * 1.05, 0, cx + ring * 1.05, 0);
      fl.addColorStop(0, rgba(CYAN, 0));
      fl.addColorStop(0.5, rgba(ICE, 0.75 * alpha));
      fl.addColorStop(1, rgba(CYAN, 0));
      ctx.fillStyle = fl;
      ctx.fillRect(cx - ring * 1.05, py - 1, ring * 2.1, 2);
      ctx.fillStyle = fl;
      ctx.globalAlpha = 0.25;
      ctx.fillRect(cx - ring * 0.7, py - 4, ring * 1.4, 8);
      ctx.globalAlpha = 1;
    };

    /* ---------- Partículas em órbita sobre a base ---------- */
    const particles = (K: number, alpha: number, dt: number) => {
      for (const p of parts) {
        p.a += p.v * dt * (1 + energy * 2.5);
        p.tw += dt * 2;
        const x = cx + p.r * Math.cos(p.a);
        const y = plat + p.r * K * Math.sin(p.a) - p.h;
        const a = (0.25 + 0.6 * (Math.sin(p.tw) * 0.5 + 0.5)) * alpha * (Math.sin(p.a) > 0 ? 1 : 0.55);
        ctx.fillStyle = rgba(ICE, a);
        ctx.fillRect(x, y, p.s, p.s);
      }
    };

    /* ---------- Feixe de luz da base até o globo ---------- */
    const beam = (alpha: number, dt: number) => {
      const top = globe - R * 0.15, bot = plat;
      const flick = 0.85 + Math.sin(t * 13) * 0.05 + Math.sin(t * 7.3) * 0.05;
      const gW = R * (0.95 + energy * 0.25), bW = 22;
      const g = ctx.createLinearGradient(0, top, 0, bot);
      g.addColorStop(0, rgba(CYAN, 0));
      g.addColorStop(0.55, rgba(CYAN, 0.16 * alpha * flick));
      g.addColorStop(1, rgba(ICE, (0.55 + energy * 0.3) * alpha * flick));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(cx - bW / 2, bot); ctx.lineTo(cx - gW / 2, top); ctx.lineTo(cx + gW / 2, top); ctx.lineTo(cx + bW / 2, bot);
      ctx.closePath(); ctx.fill();
      const core = ctx.createLinearGradient(0, top, 0, bot);
      core.addColorStop(0, rgba(ICE, 0));
      core.addColorStop(1, rgba(ICE, 0.85 * alpha));
      ctx.fillStyle = core;
      ctx.fillRect(cx - 1.2, top + R * 0.4, 2.4, bot - top - R * 0.4);
      for (const m of motes) {
        m.y += m.v * dt * (1 + energy * 3);
        if (m.y > 1) { m.y = 0; m.x = (Math.random() - 0.5) * 2; }
        const y = bot - m.y * (bot - top);
        const spread = bW / 2 + (gW / 2 - bW / 2) * m.y;
        ctx.fillStyle = rgba(ICE, (1 - m.y) * 0.8 * alpha);
        ctx.fillRect(cx + m.x * spread * 0.8, y, 1.6, 1.6);
      }
    };

    /* ---------- Globo em malha ---------- */
    const proj = (p: [number, number, number], yaw: number, pitch: number, s: number) => {
      const cy = Math.cos(yaw), sy = Math.sin(yaw);
      const x1 = p[0] * cy - p[2] * sy;
      const z1 = p[0] * sy + p[2] * cy;
      const cp = Math.cos(pitch), sp = Math.sin(pitch);
      const y2 = p[1] * cp - z1 * sp;
      const z2 = p[1] * sp + z1 * cp;
      const f = 1 + z2 * 0.07;
      return [cx + x1 * R * s * f, globe - y2 * R * s * f, z2] as const;
    };

    const sphere = (alpha: number) => {
      const s = 1 + energy * 0.05 + Math.sin(t * 1.2) * 0.006;
      const yaw = t * 0.16 + mx * 0.7;
      const pitch = -0.3 + my * 0.22;
      const r = R * s;

      sprite(sSoft, cx, globe, r * 3.4);
      sprite(sHalo, cx, globe, r * 2.9);

      const body = ctx.createRadialGradient(cx, globe + r * 0.35, r * 0.05, cx, globe, r);
      body.addColorStop(0, rgba(ICE, 0.42 * alpha));
      body.addColorStop(0.35, rgba(CYAN, 0.16 * alpha));
      body.addColorStop(0.8, rgba(BLUE, 0.1 * alpha));
      body.addColorStop(0.96, rgba(CYAN, 0.32 * alpha));
      body.addColorStop(1, rgba(CYAN, 0));
      ctx.fillStyle = body;
      ctx.beginPath(); ctx.arc(cx, globe, r, 0, TAU); ctx.fill();

      /* Grade de meridianos e paralelos: o lado de trás mais apagado. */
      const backP = new Path2D(), frontP = new Path2D();
      for (const line of grid) {
        let prev: readonly [number, number, number] | null = null;
        for (const pt of line) {
          const q = proj(pt, yaw, pitch, s);
          if (prev) {
            const path = (q[2] + prev[2]) / 2 > 0 ? frontP : backP;
            path.moveTo(prev[0], prev[1]); path.lineTo(q[0], q[1]);
          }
          prev = q;
        }
      }
      ctx.lineWidth = 0.8;
      ctx.strokeStyle = rgba(CYAN, 0.12 * alpha); ctx.stroke(backP);
      ctx.strokeStyle = rgba(CYAN, 0.38 * alpha); ctx.stroke(frontP);

      /* Malha geodésica com nós acesos. */
      const P = mesh.pts.map((p) => proj(p, yaw * 0.6 + 0.4, pitch, s * 0.985));
      const mb = new Path2D(), mf = new Path2D();
      for (const [i, j] of mesh.edges) {
        const a = P[i]!, b = P[j]!;
        const path = a[2] + b[2] > 0 ? mf : mb;
        path.moveTo(a[0], a[1]); path.lineTo(b[0], b[1]);
      }
      ctx.lineWidth = 0.7;
      ctx.strokeStyle = rgba(ICE, 0.07 * alpha); ctx.stroke(mb);
      ctx.strokeStyle = rgba(ICE, (0.3 + energy * 0.35) * alpha); ctx.stroke(mf);
      for (let k = 0; k < P.length; k++) {
        const p = P[k]!;
        if (p[2] < 0) continue;
        const tw = 0.4 + 0.6 * Math.max(0, Math.sin(t * 1.7 + k * 1.31));
        const sz = 1.2 + p[2] * 1.3;
        ctx.fillStyle = rgba(ICE, (0.35 + tw * 0.55) * alpha);
        ctx.fillRect(p[0] - sz / 2, p[1] - sz / 2, sz, sz);
      }

      /* Faixa de varredura que sobe e desce pelo globo. */
      const sc = Math.sin(t * 0.7) * 0.85;
      const yy = globe - sc * r * Math.cos(pitch);
      const rr = r * Math.sqrt(1 - sc * sc);
      ctx.strokeStyle = rgba(ICE, 0.55 * alpha);
      ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.ellipse(cx, yy, rr, rr * Math.abs(Math.sin(pitch)) + 1, 0, 0, Math.PI); ctx.stroke();
      ctx.strokeStyle = rgba(ICE, 0.15 * alpha);
      ctx.beginPath(); ctx.ellipse(cx, yy, rr, rr * Math.abs(Math.sin(pitch)) + 1, 0, Math.PI, TAU); ctx.stroke();

      /* Contorno e núcleo de luz. */
      ctx.lineWidth = hot.current ? 2.2 : 1.4;
      ctx.strokeStyle = rgba(ICE, (hot.current ? 0.9 : 0.6) * alpha);
      ctx.beginPath(); ctx.arc(cx, globe, r, 0, TAU); ctx.stroke();
      ctx.lineWidth = 1;
      sprite(sCore, cx, globe + r * 0.28, r * (1.1 + energy * 0.6), r * (0.85 + energy * 0.5));
      sprite(sCore, cx, globe, r * 0.35);

      /* Órbitas inclinadas em volta do globo. */
      for (const [rot, k, spd] of [[-0.38, 1.42, 0.6], [0.52, 1.3, -0.45]] as const) {
        ctx.save();
        ctx.translate(cx, globe);
        ctx.rotate(rot + mx * 0.05);
        ctx.strokeStyle = rgba(CYAN, 0.3 * alpha);
        ctx.setLineDash([2, 5]);
        ctx.beginPath(); ctx.ellipse(0, 0, r * k, r * k * 0.24, 0, 0, TAU); ctx.stroke();
        ctx.setLineDash([]);
        const a = t * spd;
        sprite(sCore, Math.cos(a) * r * k, Math.sin(a) * r * k * 0.24, 22);
        ctx.restore();
      }

      /* Algo aguarda a sua decisão: um satélite âmbar, a cor das ações. */
      if (sig.current.alert) {
        const a = -t * 0.5;
        const x = cx + Math.cos(a) * r * 1.18, y = globe + Math.sin(a) * r * 1.18 * 0.5;
        sprite(sAmber, x, y, 30);
      }
    };

    /* ---------- Lentes laterais e esfera menor (só na tela inicial) ---------- */
    const lens = (x: number, y: number, rot: number, alpha: number) => {
      const px = x + mx * 10, py = y + my * 6;
      const link = ctx.createLinearGradient(px, py, cx, plat);
      link.addColorStop(0, rgba(ICE, 0.35 * alpha));
      link.addColorStop(1, rgba(CYAN, 0));
      ctx.strokeStyle = link;
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(cx, plat); ctx.stroke();
      sprite(sSoft, px, py, 230, 150);
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(rot + Math.sin(t * 0.8 + x) * 0.04);
      const g = ctx.createLinearGradient(-60, -26, 60, 26);
      g.addColorStop(0, rgba('235,244,255', 0.85 * alpha));
      g.addColorStop(0.5, rgba('150,180,220', 0.55 * alpha));
      g.addColorStop(1, rgba(CYAN, 0.35 * alpha));
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.ellipse(0, 0, 64, 27, 0, 0, TAU); ctx.fill();
      ctx.strokeStyle = rgba(ICE, 0.9 * alpha);
      ctx.lineWidth = 1.6;
      ctx.stroke();
      ctx.strokeStyle = rgba(CYAN, 0.6 * alpha);
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.ellipse(0, 0, 76, 34, 0, -0.4 + t * 0.4, 1.6 + t * 0.4); ctx.stroke();
      ctx.beginPath(); ctx.ellipse(0, 0, 76, 34, 0, Math.PI - 0.4 + t * 0.4, Math.PI + 1.2 + t * 0.4); ctx.stroke();
      ctx.restore();
    };

    const miniSphere = (x: number, y: number, r: number, alpha: number) => {
      const px = x + mx * 16, py = y + my * 8;
      sprite(sSoft, px, py, r * 4.2);
      const g = ctx.createRadialGradient(px - r * 0.3, py - r * 0.3, r * 0.1, px, py, r);
      g.addColorStop(0, rgba(ICE, 0.95 * alpha));
      g.addColorStop(0.45, rgba(CYAN, 0.75 * alpha));
      g.addColorStop(1, rgba(BLUE, 0.35 * alpha));
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(px, py, r, 0, TAU); ctx.fill();
      ctx.strokeStyle = rgba(ICE, 0.35 * alpha);
      for (let k = 0; k < 4; k++) {
        const ph = ((t * 0.5 + k / 4) % 1) * Math.PI;
        ctx.beginPath(); ctx.ellipse(px, py, Math.abs(Math.cos(ph)) * r, r, 0, 0, TAU); ctx.stroke();
      }
      for (const f of [-0.5, 0, 0.5]) {
        ctx.beginPath(); ctx.ellipse(px, py + f * r, r * Math.sqrt(1 - f * f), r * 0.18, 0, 0, TAU); ctx.stroke();
      }
      ctx.strokeStyle = rgba(CYAN, 0.45 * alpha);
      ctx.beginPath(); ctx.ellipse(px, py + r * 1.05, r * 1.3, r * 0.28, 0, 0, TAU); ctx.stroke();
    };

    /* Arcos laterais que emolduram a cúpula. */
    const domeArcs = (alpha: number) => {
      const r = ring * 1.08, y = plat - 70;
      ctx.lineWidth = 1;
      for (const [a0, a1] of [[Math.PI * 0.86, Math.PI * 1.14], [-Math.PI * 0.14, Math.PI * 0.14]] as const) {
        ctx.strokeStyle = rgba(CYAN, 0.35 * alpha);
        ctx.beginPath(); ctx.arc(cx, y, r, a0, a1); ctx.stroke();
        ctx.strokeStyle = rgba(BLUE, 0.6 * alpha);
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(cx, y, r - 10, a0 + 0.05, a0 + 0.12); ctx.stroke();
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let k = 0; k <= 24; k++) {
          const a = a0 + ((a1 - a0) * k) / 24;
          ctx.moveTo(cx + Math.cos(a) * (r + 4), y + Math.sin(a) * (r + 4));
          ctx.lineTo(cx + Math.cos(a) * (r + (k % 6 === 0 ? 14 : 8)), y + Math.sin(a) * (r + (k % 6 === 0 ? 14 : 8)));
        }
        ctx.strokeStyle = rgba(CYAN, 0.3 * alpha);
        ctx.stroke();
      }
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000) * (reduce ? 0.12 : 1);
      last = now;
      t += dt;
      const s = sig.current;
      mx += (tmx - mx) * 0.06;
      my += (tmy - my) * 0.06;
      const target = s.listening ? 0.35 + s.level * 0.9 : s.speaking ? 0.45 + Math.abs(Math.sin(t * 9)) * 0.3 : hot.current ? 0.2 : 0;
      energy += (target - energy) * 0.12;
      if ((s.listening || s.speaking) && !reduce && t - lastRipple > (s.speaking ? 0.45 : 0.8 - s.level * 0.5)) {
        ripples.push({ born: t }); lastRipple = t;
        if (ripples.length > 8) ripples.shift();
      }

      const alpha = s.online ? 1 : 0.42;
      const K = 0.23 + my * 0.025;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';
      if (home) domeArcs(alpha);
      platform(K, alpha, 0.35 + energy * 1.4);
      particles(K, alpha, dt);
      if (home) {
        lens(cx - 410, plat - 26, 0.55, alpha);
        lens(cx + 410, plat - 26, -0.55, alpha);
      }
      beam(alpha, dt);
      sphere(alpha);
      if (home) miniSphere(cx - 300, plat + 52, 36, alpha);
      ctx.globalCompositeOperation = 'source-over';
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', size);
      window.removeEventListener('pointermove', onMove);
    };
  }, [variant]);

  const pct = (v: number, of: number) => `${(v / of) * 100}%`;
  return (
    <div className={`holo holo-${variant}`}>
      <canvas ref={cvRef} className="holo-canvas" aria-hidden="true" />
      {onCore && (
        <button type="button" className="holo-core" aria-label={coreLabel} title={coreLabel}
          style={{ left: pct(V.cx - V.R, V.w), top: pct(V.globe - V.R, V.h), width: pct(V.R * 2, V.w), height: pct(V.R * 2, V.h) }}
          onPointerEnter={() => { hot.current = true; }} onPointerLeave={() => { hot.current = false; }}
          onFocus={() => { hot.current = true; }} onBlur={() => { hot.current = false; }}
          onClick={onCore} />
      )}
    </div>
  );
}
